import { useState } from "react";
import api from "../api";
import {
  Field,
  DateInput,
  Segmented,
  Actions,
  PrimaryButton,
  GhostButton,
  ResultBanner,
  BusyNote,
  CardIntro,
} from "./ui/Form";

/**
 * Weekly Access database update (ROADMAP B2, Amy).
 *
 * Three datasets behind a pill, replacing the FieldSeeker exports and the Excel
 * wrangling that followed. Writing to Access stays manual: the prepared rows go
 * to the clipboard.
 *
 * Inspections and treatments are weekly appends -- one Saturday-to-Friday window
 * pasted onto the bottom of the table. Sites is different: FieldSeeker and
 * Access do not sync, so the table is emptied and rebuilt in full every week,
 * and the three location layers are combined into one paste rather than three.
 *
 * Copying as TSV rather than CSV is deliberate: Access and Excel split
 * tab-separated text across columns on paste, where comma-separated text lands
 * in a single cell.
 */

type Dataset = "inspections" | "treatments" | "sites";
type SiteLayer = "points" | "lines" | "polygons";
/** Backed up as a shapefile and nothing else: not part of the Sites rebuild. */
type BackupOnlyLayer = "stormdrain";

const SITE_LAYERS: [SiteLayer, string][] = [
  ["points", "Points"],
  ["lines", "Lines"],
  ["polygons", "Polygons"],
];

/**
 * The Saturday->Friday window, anchored to the Saturday that opened the current
 * week -- so it is the week in progress, not the one before it. Mirrors
 * access_update.default_week(); keep the two in step.
 */
function defaultWeek(): { start: string; end: string } {
  const today = new Date();
  const daysSinceSaturday = (today.getDay() - 6 + 7) % 7; // Sun=0 .. Sat=6
  const saturday = new Date(today);
  saturday.setDate(today.getDate() - daysSinceSaturday);
  const friday = new Date(saturday);
  friday.setDate(saturday.getDate() + 6);
  const iso = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return { start: iso(saturday), end: iso(friday) };
}

interface PulledData {
  count: number;
  fields: string[];
  /** Positional: the inspections set has two columns named Time. */
  rows: string[][];
  start_date?: string;
  end_date?: string;
  dataset?: string;
  table?: string;
  counts?: Record<string, number>;
}

export default function AccessWeeklyUpdateCard() {
  const initial = defaultWeek();
  const [dataset, setDataset] = useState<Dataset>("inspections");
  const [startDate, setStartDate] = useState(initial.start);
  const [endDate, setEndDate] = useState(initial.end);
  const [busy, setBusy] = useState("");
  const [result, setResult] = useState<any>(null);
  const [data, setData] = useState<PulledData | null>(null);
  const [copied, setCopied] = useState(false);
  /**
   * Sites defaults to the full snapshot, because that is what the weekly
   * rebuild needs. The date range is for inspecting what changed -- pasting a
   * filtered subset into an emptied table would lose every unchanged site.
   */
  const [siteScope, setSiteScope] = useState<"all" | "edited">("all");

  const isSites = dataset === "sites";
  const targetTable = isSites
    ? "Sites"
    : dataset === "treatments"
    ? "Treatment"
    : "MosquitoInspection";

  const pickDataset = (next: Dataset) => {
    setDataset(next);
    setData(null);
    setResult(null);
    setCopied(false);
    setSiteScope("all");
  };

  const download = async (url: string, body: any, filename: string, label: string) => {
    setBusy(label);
    try {
      const res = await api.post(url, body, { responseType: "blob" });
      const href = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = href;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(href);
    } catch (err: any) {
      setResult({ status: "failure", message: err?.response?.data?.message || err.message });
    } finally {
      setBusy("");
    }
  };

  const fetchRecords = async () => {
    setBusy(isSites ? "Reading all three site layers…" : "Reading from FieldSeeker…");
    setResult(null);
    setData(null);
    setCopied(false);
    try {
      const res = isSites
        ? await api.post("/sites-prepared", siteScope === "edited"
            ? { start_date: startDate, end_date: endDate } : {})
        : await api.post("/access-weekly-records", {
            dataset,
            start_date: startDate,
            end_date: endDate,
          });
      setData(res.data);
      if (!res.data.count) {
        setResult({ status: "failure", message: "No records found." });
      }
    } catch (err: any) {
      setResult({ status: "failure", message: err?.response?.data?.message || err.message });
    } finally {
      setBusy("");
    }
  };

  const stamp = new Date().toISOString().slice(0, 10);

  const downloadPrepared = () =>
    isSites
      ? download(
          "/sites-prepared-csv",
          siteScope === "edited" ? { start_date: startDate, end_date: endDate } : {},
          `Sites_Prepared_${stamp}.csv`,
          "Building prepared CSV…"
        )
      : download(
          "/access-weekly-records-csv",
          { dataset, start_date: startDate, end_date: endDate, prepared: true },
          `${targetTable}_Prepared_${startDate}_to_${endDate}.csv`,
          "Building prepared CSV…"
        );

  const downloadRawWeek = () =>
    download(
      "/access-weekly-records-csv",
      { dataset, start_date: startDate, end_date: endDate, prepared: false },
      `${targetTable}_Raw_${startDate}_to_${endDate}.csv`,
      "Building raw CSV…"
    );

  const downloadBackupOnly = (layer: BackupOnlyLayer) =>
    download("/sites-raw-shapefile", { layer }, `${layer}_${stamp}.zip`, "Building storm drain shapefile…");

  const downloadLayer = (layer: SiteLayer, kind: "csv" | "shp") =>
    kind === "csv"
      ? download("/sites-raw-csv", { layer }, `${layer}_${stamp}.csv`, `Building ${layer} CSV…`)
      : download("/sites-raw-shapefile", { layer }, `${layer}_${stamp}.zip`, `Building ${layer} shapefile…`);

  /**
   * Data rows only, tab-separated. No header row: this pastes onto an existing
   * Access table, where a header line would become a record.
   */
  const copyForAccess = async () => {
    if (!data) return;
    const cell = (v: string) => String(v ?? "").replace(/[\t\r\n]+/g, " ");
    const text = data.rows.map((row) => row.map(cell).join("\t")).join("\r\n");
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 5000);
    } catch {
      setResult({
        status: "failure",
        message: "The browser blocked the clipboard. Use the prepared CSV download instead.",
      });
    }
  };

  const preview = data?.rows.slice(0, 10) ?? [];

  return (
    <div className="space-y-4">
      <CardIntro>
        {isSites ? (
          <>
            Rebuilds the <b>Sites</b> table from all three location layers in one go. Empty the
            table in Access first, then paste — this replaces the whole thing rather than
            appending. Grab the per-layer backups below before you do.
          </>
        ) : (
          <>
            Pulls one week of records straight from FieldSeeker and applies the usual clean-up.
            Keep the raw CSV as the record copy, then paste the prepared rows onto the bottom of
            the <b>{targetTable}</b> table in Access.
          </>
        )}
      </CardIntro>

      <Field label="Dataset">
        <Segmented<Dataset>
          value={dataset}
          onChange={pickDataset}
          disabled={!!busy}
          options={[
            ["inspections", "Inspections"],
            ["treatments", "Treatments"],
            ["sites", "Sites"],
          ]}
        />
      </Field>

      {isSites && (
        <Field
          label="Scope"
          hint={
            siteScope === "all"
              ? "Every site — what the weekly rebuild needs."
              : "Only sites edited in the range. For review, not for the rebuild."
          }
        >
          <Segmented<"all" | "edited">
            value={siteScope}
            onChange={(v) => { setSiteScope(v); setData(null); setResult(null); }}
            disabled={!!busy}
            options={[
              ["all", "All sites"],
              ["edited", "Edited in range"],
            ]}
          />
        </Field>
      )}

      {(!isSites || siteScope === "edited") && (
        <div className="flex gap-3 flex-wrap">
          <Field label={isSites ? "Edited from" : "From (Saturday)"}>
            <DateInput value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </Field>
          <Field label={isSites ? "Edited to" : "To (Friday)"}>
            <DateInput value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </Field>
        </div>
      )}

      {isSites && (
        <Field label="Backups — every row and field, straight from AGOL">
          <div className="flex flex-col gap-1.5">
            {SITE_LAYERS.map(([key, label]) => (
              <div key={key} className="flex items-center gap-2">
                <span className="text-[13px] font-semibold w-24">{label}</span>
                <GhostButton onClick={() => downloadLayer(key, "csv")} disabled={!!busy}>
                  ⬇ CSV
                </GhostButton>
                <GhostButton onClick={() => downloadLayer(key, "shp")} disabled={!!busy}>
                  ⬇ Shapefile
                </GhostButton>
              </div>
            ))}
            <div className="flex items-center gap-2">
              <span className="text-[13px] font-semibold w-24">Storm Drain</span>
              <GhostButton onClick={() => downloadBackupOnly("stormdrain")} disabled={!!busy}>
                ⬇ Shapefile
              </GhostButton>
            </div>
          </div>
        </Field>
      )}

      <Actions>
        <PrimaryButton onClick={fetchRecords} busy={!!busy} busyLabel="Working…" disabled={!!busy}>
          Get Records
        </PrimaryButton>
        {data && !isSites && (
          <GhostButton onClick={downloadRawWeek} disabled={!!busy}>
            ⬇ Download raw CSV
          </GhostButton>
        )}
      </Actions>

      {busy && <BusyNote>{busy}</BusyNote>}
      <ResultBanner result={result} />

      {data && data.count > 0 && (
        <div className="space-y-3">
          <p className="text-[13px] font-semibold">
            {data.count.toLocaleString()} row{data.count === 1 ? "" : "s"}
            {isSites && data.counts
              ? ` · ${SITE_LAYERS.map(([k, l]) => `${l.toLowerCase()} ${(data.counts![k] ?? 0).toLocaleString()}`).join(" · ")}` +
                (siteScope === "edited" ? ` · edited ${startDate} to ${endDate}` : "")
              : ` · ${data.start_date} to ${data.end_date}`}
          </p>

          {isSites && siteScope === "all" && (
            <div className="border-l-4 border-wnv bg-wnv-tint rounded-sm2 px-4 py-3 text-[13px]">
              <b className="text-wnv">This replaces the table, it does not add to it.</b> Delete
              the existing rows in the Access <b>Sites</b> table before pasting, or you will end
              up with every site twice.
            </div>
          )}
          {isSites && siteScope === "edited" && (
            <div className="border-l-4 border-wnv bg-wnv-tint rounded-sm2 px-4 py-3 text-[13px]">
              <b className="text-wnv">Not the weekly rebuild.</b> These are only the sites edited
              between {data.start_date ?? startDate} and {data.end_date ?? endDate}. Emptying the
              Sites table and pasting this would drop every site that did not change.
            </div>
          )}

          <Actions>
            <PrimaryButton onClick={copyForAccess} disabled={!!busy}>
              {copied
                ? `✓ Copied ${data.count.toLocaleString()} rows — paste into ${targetTable}`
                : `📋 Copy rows for ${targetTable}`}
            </PrimaryButton>
            <GhostButton onClick={downloadPrepared} disabled={!!busy}>
              ⬇ Download prepared CSV
            </GhostButton>
          </Actions>

          <p className="text-ink-faint text-[12px]">
            Copies the data rows only, without a header line.
          </p>

          <div className="overflow-x-auto border border-line rounded-sm2">
            <table className="text-[12px] w-full border-collapse">
              <thead>
                <tr className="bg-paper-2">
                  {data.fields.map((f, i) => (
                    <th
                      key={i}
                      className="text-left font-mono font-semibold px-2 py-1.5 whitespace-nowrap border-b border-line"
                    >
                      {f}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.map((row, i) => (
                  <tr key={i} className="border-b border-line last:border-0">
                    {row.map((value, j) => (
                      <td
                        key={j}
                        className="px-2 py-1 whitespace-nowrap max-w-[22rem] truncate"
                        title={value}
                      >
                        {value}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {data.count > preview.length && (
            <p className="text-ink-faint text-[12px]">
              Showing the first {preview.length} of {data.count.toLocaleString()}. Copy and download
              cover all of them.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
